// ============================================================================
// Blue Ocean Motion — legal content (Phase 9: Google Play compliance)
// EN + PL. Text reflects the ACTUAL implementation only:
// - localStorage: bom_profile_id (random UUID), bom_lang (language)
// - MongoDB: profiles, activities, posts, kudos, follows,
//   challenge_members, challenge_celebrations
// - no ads, no third-party analytics SDKs, no sale of data, no payments
// ============================================================================

export const LEGAL_META = {
  operator: "Marek Wr\u00f3blewski (Blue Ocean Motion)",
  email: "blueoceanmotion.review2026@gmail.com",
  url: "https://motion.pewnoscsiebie.com",
  updated: "2026-08-17",
};

export const legalUi = {
  en: {
    updatedLabel: "Last updated",
    contactLabel: "Contact",
    openApp: "Open Blue Ocean Motion",
    privacy: "Privacy Policy",
    terms: "Terms of Use",
    deleteData: "Data Deletion",
  },
  pl: {
    updatedLabel: "Ostatnia aktualizacja",
    contactLabel: "Kontakt",
    openApp: "Otw\u00f3rz Blue Ocean Motion",
    privacy: "Polityka prywatno\u015bci",
    terms: "Regulamin",
    deleteData: "Usuwanie danych",
  },
};

// ---------------------------------------------------------------- PRIVACY
export const privacyContent = {
  en: {
    title: "Privacy Policy",
    sections: [
      {
        h: "1. Who we are",
        p: [
          `Blue Ocean Motion is a movement-tracking and motivation web application available at ${LEGAL_META.url}. The service is operated by ${LEGAL_META.operator}.`,
          `For any privacy matter you can contact us at ${LEGAL_META.email}.`,
        ],
      },
      {
        h: "2. No traditional account",
        p: [
          "Blue Ocean Motion does not use username/password accounts and does not ask for your e-mail address to use the app. When you start the app for the first time, a random profile identifier (UUID) is generated and stored in your browser. This identifier links your device to your profile data.",
          "Because access to your profile depends on your device and browser, anyone with access to your unlocked device and browser profile could open your in-app profile. Please keep your device secure.",
        ],
      },
      {
        h: "3. Data stored in your browser (local storage)",
        p: ["The application stores only two values in your browser's localStorage:"],
        list: [
          "bom_profile_id \u2014 the random identifier of your local profile (an opaque UUID; it contains no personal details by itself)",
          "bom_lang \u2014 your language preference (English or Polish)",
        ],
        after: [
          "The application itself does not set advertising or analytics cookies. Our hosting infrastructure may set strictly technical cookies needed for security and load balancing.",
        ],
      },
      {
        h: "4. Data stored on our servers",
        p: [
          "The following data is stored in our database (MongoDB) on our hosting infrastructure and is linked to your profile identifier:",
        ],
        list: [
          "Profile: the name you enter, optional public display name, optional bio, your goal, preferred activity, level, weekly goal (minutes), privacy setting and creation date",
          "Activities: activity type, duration, intensity, optional mood before/after, optional note and the date/time of logging",
          "Community posts (only if you enable \"Share to Community\" for an activity): activity type, duration, your goal, your display name, the optional note as caption and the kudos count",
          "Kudos: which community posts you gave kudos to",
          "Follows: which profiles you follow and which profiles follow you",
          "Challenges: which challenges you joined, your progress and whether a completion celebration was already shown",
        ],
      },
      {
        h: "5. Purposes of processing",
        p: ["We process this data exclusively to:"],
        list: [
          "provide the core features: activity logging, statistics, streaks, weekly recap and challenges",
          "provide the community features you opt into: shared posts, kudos and following",
          "remember your language preference",
          "keep the service secure and reliable (standard technical server logs, which may temporarily include IP addresses, are used only for security and operations)",
        ],
      },
      {
        h: "6. Legal bases for processing (GDPR)",
        p: ["We rely only on the following legal bases, matching how the application actually works:"],
        list: [
          "Art. 6(1)(b) GDPR (performance of a contract \u2014 providing the service you request): your profile data, your activities and the statistics computed from them, which are necessary to provide the core Blue Ocean Motion features",
          "Art. 6(1)(b) GDPR for the optional community features you actively initiate yourself (publishing a post, giving kudos, following a profile, joining a challenge): this data is processed only because, and for as long as, you choose to use those features",
          "Art. 6(1)(f) GDPR (our legitimate interest in protecting, securing and maintaining the service): standard technical and security server logs, which may temporarily include IP addresses",
        ],
      },
      {
        h: "7. Sharing of data",
        p: [
          "We do not sell your data. The application contains no advertising networks and no third-party analytics SDKs.",
          "Other users of Blue Ocean Motion can see only what you choose to share: community posts you publish (with your display name), their kudos counts, and the fact that a profile can be followed. If your profile is set to private, nothing is shared unless you explicitly enable sharing for an activity.",
          "Infrastructure providers (cloud hosting and database hosting) process data on our behalf solely to run the service.",
          "Hosting location and international transfers: the service runs on cloud infrastructure of our hosting provider, whose data centers may be located outside the European Economic Area (including the United States). Where personal data is transferred outside the EEA, the transfer is protected by the safeguards required under Chapter V of the GDPR \u2014 an adequacy decision of the European Commission (such as the EU\u2013U.S. Data Privacy Framework, where the provider is certified) or Standard Contractual Clauses implemented by the relevant infrastructure providers.",
        ],
      },
      {
        h: "8. Security",
        p: [
          "All traffic between your device and our servers is encrypted with HTTPS/TLS. Database access is restricted. The application processes no payment data.",
        ],
      },
      {
        h: "9. Retention",
        p: [
          "Your data is kept until you delete it. You can edit or delete individual activities at any time in the Progress screen.",
          "Full deletion: the \"Delete profile and data\" action in the Profile screen permanently removes all server-side data listed in section 4 from the active production database, immediately.",
          "Technical server logs (which may temporarily include IP addresses) are kept only for ongoing security and operations: they are automatically rotated and overwritten in the normal logging cycle and are not archived by the application.",
          "We do not create additional backup copies of our own. The hosting platform may maintain short-lived, automatically overwritten infrastructure-level copies (backups or replicas) for reliability; deleted data is not restored from such copies.",
          "Note: the separate \"Start over\" action only disconnects the profile from your device \u2014 it does NOT delete server-side data. Use \"Delete profile and data\" for real deletion.",
        ],
      },
      {
        h: "10. Your rights (GDPR)",
        p: [
          "Under the EU General Data Protection Regulation you have the right of access, rectification, erasure, restriction of processing, objection and data portability.",
          "You can exercise deletion yourself at any time in the app (Profile \u2192 Delete profile and data) or contact us at the e-mail address below for any request.",
          "You also have the right to lodge a complaint with a supervisory authority \u2014 in Poland: the President of the Personal Data Protection Office (Prezes Urz\u0119du Ochrony Danych Osobowych, UODO).",
        ],
      },
      {
        h: "11. Children",
        p: [
          "Blue Ocean Motion is not directed at children under 13 years of age. The service provides general movement motivation only.",
        ],
      },
      {
        h: "12. Changes to this policy",
        p: [
          "We may update this Privacy Policy. The current version, with its update date, is always available at this address.",
        ],
      },
      {
        h: "13. Contact",
        p: [
          `${LEGAL_META.operator}`,
          `E-mail: ${LEGAL_META.email}`,
        ],
      },
    ],
  },
  pl: {
    title: "Polityka prywatno\u015bci",
    sections: [
      {
        h: "1. Kim jeste\u015bmy",
        p: [
          `Blue Ocean Motion to aplikacja internetowa do \u015bledzenia ruchu i motywacji, dost\u0119pna pod adresem ${LEGAL_META.url}. Us\u0142uga jest prowadzona przez: ${LEGAL_META.operator}.`,
          `W sprawach prywatno\u015bci mo\u017cesz skontaktowa\u0107 si\u0119 z nami pod adresem ${LEGAL_META.email}.`,
        ],
      },
      {
        h: "2. Brak tradycyjnego konta",
        p: [
          "Blue Ocean Motion nie u\u017cywa kont z loginem i has\u0142em i nie wymaga adresu e-mail. Przy pierwszym uruchomieniu aplikacja generuje losowy identyfikator profilu (UUID), kt\u00f3ry jest zapisywany w Twojej przegl\u0105darce. Ten identyfikator \u0142\u0105czy Twoje urz\u0105dzenie z danymi profilu.",
          "Poniewa\u017c dost\u0119p do profilu zale\u017cy od urz\u0105dzenia i przegl\u0105darki, ka\u017cda osoba z dost\u0119pem do Twojego odblokowanego urz\u0105dzenia mo\u017ce otworzy\u0107 Tw\u00f3j profil w aplikacji. Dbaj o bezpiecze\u0144stwo swojego urz\u0105dzenia.",
        ],
      },
      {
        h: "3. Dane zapisywane w przegl\u0105darce (local storage)",
        p: ["Aplikacja zapisuje w localStorage przegl\u0105darki wy\u0142\u0105cznie dwie warto\u015bci:"],
        list: [
          "bom_profile_id \u2014 losowy identyfikator Twojego lokalnego profilu (nieprzejrzysty UUID; sam w sobie nie zawiera danych osobowych)",
          "bom_lang \u2014 preferowany j\u0119zyk (angielski lub polski)",
        ],
        after: [
          "Sama aplikacja nie ustawia plik\u00f3w cookie reklamowych ani analitycznych. Infrastruktura hostingowa mo\u017ce ustawia\u0107 wy\u0142\u0105cznie techniczne pliki cookie potrzebne do bezpiecze\u0144stwa i r\u00f3wnowa\u017cenia obci\u0105\u017cenia.",
        ],
      },
      {
        h: "4. Dane zapisywane na naszych serwerach",
        p: [
          "Nast\u0119puj\u0105ce dane s\u0105 przechowywane w naszej bazie danych (MongoDB) na infrastrukturze hostingowej i s\u0105 powi\u0105zane z identyfikatorem Twojego profilu:",
        ],
        list: [
          "Profil: wpisane imi\u0119, opcjonalna publiczna nazwa wy\u015bwietlana, opcjonalne bio, cel, preferowana aktywno\u015b\u0107, poziom, cel tygodniowy (minuty), ustawienie prywatno\u015bci i data utworzenia",
          "Aktywno\u015bci: typ aktywno\u015bci, czas trwania, intensywno\u015b\u0107, opcjonalny nastr\u00f3j przed/po, opcjonalna notatka oraz data i godzina zapisu",
          "Wpisy w spo\u0142eczno\u015bci (tylko je\u015bli w\u0142\u0105czysz \u201eUdost\u0119pnij spo\u0142eczno\u015bci\u201d dla aktywno\u015bci): typ aktywno\u015bci, czas trwania, Tw\u00f3j cel, nazwa wy\u015bwietlana, opcjonalna notatka jako opis oraz liczba kudos",
          "Kudos: kt\u00f3rym wpisom da\u0142e\u015b/da\u0142a\u015b kudos",
          "Obserwowanie: kt\u00f3re profile obserwujesz i kt\u00f3re profile obserwuj\u0105 Ciebie",
          "Wyzwania: do kt\u00f3rych wyzwa\u0144 do\u0142\u0105czy\u0142e\u015b/do\u0142\u0105czy\u0142a\u015b, Tw\u00f3j post\u0119p oraz informacja, czy celebracja uko\u0144czenia zosta\u0142a ju\u017c wy\u015bwietlona",
        ],
      },
      {
        h: "5. Cele przetwarzania",
        p: ["Przetwarzamy te dane wy\u0142\u0105cznie po to, aby:"],
        list: [
          "zapewni\u0107 podstawowe funkcje: zapisywanie aktywno\u015bci, statystyki, serie, podsumowanie tygodnia i wyzwania",
          "zapewni\u0107 funkcje spo\u0142eczno\u015bciowe, na kt\u00f3re si\u0119 zdecydujesz: udost\u0119pnione wpisy, kudos i obserwowanie",
          "zapami\u0119ta\u0107 preferowany j\u0119zyk",
          "utrzyma\u0107 bezpiecze\u0144stwo i niezawodno\u015b\u0107 us\u0142ugi (standardowe techniczne logi serwera, kt\u00f3re mog\u0105 tymczasowo zawiera\u0107 adresy IP, s\u0142u\u017c\u0105 wy\u0142\u0105cznie bezpiecze\u0144stwu i utrzymaniu)",
        ],
      },
      {
        h: "6. Podstawy prawne przetwarzania (RODO)",
        p: ["Opieramy si\u0119 wy\u0142\u0105cznie na nast\u0119puj\u0105cych podstawach prawnych, zgodnych z faktycznym dzia\u0142aniem aplikacji:"],
        list: [
          "art. 6 ust. 1 lit. b RODO (wykonanie umowy \u2014 \u015bwiadczenie us\u0142ugi, o kt\u00f3r\u0105 prosisz): dane profilu, aktywno\u015bci i obliczane z nich statystyki, niezb\u0119dne do dzia\u0142ania podstawowych funkcji Blue Ocean Motion",
          "art. 6 ust. 1 lit. b RODO dla opcjonalnych funkcji spo\u0142eczno\u015bciowych, kt\u00f3re sam(a) inicjujesz (publikacja wpisu, kudos, obserwowanie profilu, do\u0142\u0105czenie do wyzwania): te dane s\u0105 przetwarzane tylko dlatego, \u017ce \u2014 i tak d\u0142ugo, jak \u2014 korzystasz z tych funkcji",
          "art. 6 ust. 1 lit. f RODO (nasz prawnie uzasadniony interes polegaj\u0105cy na ochronie, zabezpieczaniu i utrzymaniu us\u0142ugi): standardowe techniczne logi serwera, kt\u00f3re mog\u0105 tymczasowo zawiera\u0107 adresy IP",
        ],
      },
      {
        h: "7. Udost\u0119pnianie danych",
        p: [
          "Nie sprzedajemy Twoich danych. Aplikacja nie zawiera sieci reklamowych ani zewn\u0119trznych narz\u0119dzi analitycznych.",
          "Inni u\u017cytkownicy Blue Ocean Motion widz\u0105 tylko to, co sam(a) udost\u0119pnisz: opublikowane wpisy w spo\u0142eczno\u015bci (z nazw\u0105 wy\u015bwietlan\u0105), liczb\u0119 kudos oraz mo\u017cliwo\u015b\u0107 obserwowania profilu. Je\u015bli profil jest prywatny, nic nie jest udost\u0119pniane, dop\u00f3ki sam(a) nie w\u0142\u0105czysz udost\u0119pniania dla aktywno\u015bci.",
          "Dostawcy infrastruktury (hosting w chmurze i hosting bazy danych) przetwarzaj\u0105 dane w naszym imieniu wy\u0142\u0105cznie w celu utrzymania us\u0142ugi.",
          "Lokalizacja hostingu i transfery mi\u0119dzynarodowe: us\u0142uga dzia\u0142a na infrastrukturze chmurowej naszego dostawcy hostingu, kt\u00f3rego centra danych mog\u0105 znajdowa\u0107 si\u0119 poza Europejskim Obszarem Gospodarczym (w tym w Stanach Zjednoczonych). Je\u017celi dane osobowe s\u0105 przekazywane poza EOG, transfer jest chroniony zabezpieczeniami wymaganymi w rozdziale V RODO \u2014 decyzj\u0105 Komisji Europejskiej stwierdzaj\u0105c\u0105 odpowiedni stopie\u0144 ochrony (np. ramami ochrony danych UE\u2013USA, Data Privacy Framework, gdy dostawca posiada certyfikacj\u0119) lub standardowymi klauzulami umownymi stosowanymi przez odpowiednich dostawc\u00f3w infrastruktury.",
        ],
      },
      {
        h: "8. Bezpiecze\u0144stwo",
        p: [
          "Ca\u0142y ruch mi\u0119dzy Twoim urz\u0105dzeniem a naszymi serwerami jest szyfrowany (HTTPS/TLS). Dost\u0119p do bazy danych jest ograniczony. Aplikacja nie przetwarza danych p\u0142atniczych.",
        ],
      },
      {
        h: "9. Okres przechowywania",
        p: [
          "Twoje dane s\u0105 przechowywane, dop\u00f3ki ich nie usuniesz. Pojedyncze aktywno\u015bci mo\u017cesz edytowa\u0107 lub usuwa\u0107 w ka\u017cdej chwili na ekranie Post\u0119p.",
          "Pe\u0142ne usuni\u0119cie: dzia\u0142anie \u201eUsu\u0144 profil i dane\u201d na ekranie Profil trwale i natychmiast usuwa z aktywnej produkcyjnej bazy danych wszystkie dane serwerowe wymienione w punkcie 4.",
          "Techniczne logi serwera (kt\u00f3re mog\u0105 tymczasowo zawiera\u0107 adresy IP) s\u0105 przechowywane wy\u0142\u0105cznie na potrzeby bie\u017c\u0105cego bezpiecze\u0144stwa i utrzymania: s\u0105 automatycznie rotowane i nadpisywane w normalnym cyklu logowania i nie s\u0105 archiwizowane przez aplikacj\u0119.",
          "Nie tworzymy w\u0142asnych dodatkowych kopii zapasowych. Platforma hostingowa mo\u017ce utrzymywa\u0107 kr\u00f3tkotrwa\u0142e, automatycznie nadpisywane kopie infrastrukturalne (backupy lub repliki) dla niezawodno\u015bci; usuni\u0119te dane nie s\u0105 z takich kopii przywracane.",
          "Uwaga: osobne dzia\u0142anie \u201eZacznij od nowa\u201d jedynie od\u0142\u0105cza profil od Twojego urz\u0105dzenia \u2014 NIE usuwa danych z serwera. Do prawdziwego usuni\u0119cia u\u017cyj \u201eUsu\u0144 profil i dane\u201d.",
        ],
      },
      {
        h: "10. Twoje prawa (RODO)",
        p: [
          "Zgodnie z RODO masz prawo do: dost\u0119pu do danych, sprostowania, usuni\u0119cia, ograniczenia przetwarzania, sprzeciwu oraz przenoszenia danych.",
          "Usuni\u0119cie mo\u017cesz wykona\u0107 samodzielnie w aplikacji (Profil \u2192 Usu\u0144 profil i dane) lub skontaktowa\u0107 si\u0119 z nami e-mailem w ka\u017cdej sprawie.",
          "Masz r\u00f3wnie\u017c prawo wnie\u015b\u0107 skarg\u0119 do organu nadzorczego \u2014 w Polsce: Prezesa Urz\u0119du Ochrony Danych Osobowych (UODO).",
        ],
      },
      {
        h: "11. Dzieci",
        p: [
          "Blue Ocean Motion nie jest skierowana do dzieci poni\u017cej 13. roku \u017cycia. Us\u0142uga zapewnia wy\u0142\u0105cznie og\u00f3ln\u0105 motywacj\u0119 do ruchu.",
        ],
      },
      {
        h: "12. Zmiany polityki",
        p: [
          "Mo\u017cemy aktualizowa\u0107 niniejsz\u0105 Polityk\u0119 prywatno\u015bci. Aktualna wersja wraz z dat\u0105 aktualizacji jest zawsze dost\u0119pna pod tym adresem.",
        ],
      },
      {
        h: "13. Kontakt",
        p: [
          `${LEGAL_META.operator}`,
          `E-mail: ${LEGAL_META.email}`,
        ],
      },
    ],
  },
};

// ------------------------------------------------------------------ TERMS
export const termsContent = {
  en: {
    title: "Terms of Use",
    sections: [
      {
        h: "1. About the service",
        p: [
          `Blue Ocean Motion (${LEGAL_META.url}) is a web application for tracking everyday movement, building motivation and joining simple community challenges. The service is operated by ${LEGAL_META.operator} (\"we\").`,
          "By using the application you accept these Terms of Use.",
        ],
      },
      {
        h: "2. Not medical advice",
        p: [
          "Blue Ocean Motion does not provide medical diagnosis or medical advice. All content \u2014 including statistics, streaks, recaps, challenges and motivational messages \u2014 is of a general, motivational nature only.",
          "Consult a qualified physician before starting or changing physical activity, especially if you have any health condition. Stop exercising immediately if you feel pain, dizziness or discomfort.",
        ],
      },
      {
        h: "3. Your responsibility",
        p: [
          "You use the application and perform physical activity at your own responsibility and within your own abilities.",
          "You are responsible for the content you enter (including notes and your display name) and for securing the device on which your local profile is stored. The app uses a device-linked profile without a password \u2014 anyone with access to your device and browser may access your profile.",
        ],
      },
      {
        h: "4. Community conduct",
        p: [
          "Community features (shared posts, kudos, following, challenges) must be used respectfully. It is prohibited to publish content that is unlawful, offensive, hateful, discriminatory, misleading or spam.",
          "We may remove content or profiles that violate these rules, and restrict access to community features in case of abuse.",
        ],
      },
      {
        h: "5. Availability and limitations",
        p: [
          "The service is provided \"as is\" and \"as available\". We may change, suspend or discontinue any feature.",
          "We make reasonable efforts to keep the service available and the data safe, but we do not guarantee uninterrupted availability. The application currently does not provide a data export feature. Deleting your profile permanently removes your data (see the Privacy Policy and the Data Deletion page).",
        ],
      },
      {
        h: "6. Intellectual property",
        p: [
          "The application, its design, branding (including the names \"Blue Ocean Motion\" and \"Motion Card\") and all related materials are owned by the operator.",
          "You retain rights to the content you create (for example activity notes). By enabling sharing you grant us a non-exclusive licence to display that content within the community features of the application.",
        ],
      },
      {
        h: "7. Liability",
        p: [
          "To the maximum extent permitted by law, we are not liable for indirect damages or for consequences of physical activity performed by the user. Nothing in these Terms excludes liability that cannot be excluded under mandatory provisions of Polish law, including consumer rights.",
        ],
      },
      {
        h: "8. Governing law",
        p: [
          "These Terms are governed by Polish law.",
          "If you use the service as a consumer, this choice of law does not deprive you of the protection granted by mandatory provisions of the law of the country of your habitual residence. You always retain all rights granted to you by applicable mandatory consumer law.",
          "Court jurisdiction for any dispute is determined by applicable mandatory law. In particular, nothing in these Terms requires a consumer to bring proceedings exclusively before Polish courts where applicable law allows the consumer to sue, or to be sued, before the courts of their habitual residence.",
        ],
      },
      {
        h: "9. Changes to these terms",
        p: [
          "We may update these Terms. The current version, with its update date, is always available at this address. Continued use of the service after changes means acceptance of the updated Terms.",
        ],
      },
      {
        h: "10. Contact",
        p: [`${LEGAL_META.operator}`, `E-mail: ${LEGAL_META.email}`],
      },
    ],
  },
  pl: {
    title: "Regulamin",
    sections: [
      {
        h: "1. O us\u0142udze",
        p: [
          `Blue Ocean Motion (${LEGAL_META.url}) to aplikacja internetowa do \u015bledzenia codziennego ruchu, budowania motywacji i udzia\u0142u w prostych wyzwaniach spo\u0142eczno\u015bci. Us\u0142uga jest prowadzona przez: ${LEGAL_META.operator} (\u201emy\u201d).`,
          "Korzystaj\u0105c z aplikacji, akceptujesz niniejszy Regulamin.",
        ],
      },
      {
        h: "2. To nie jest porada medyczna",
        p: [
          "Blue Ocean Motion nie zapewnia diagnozy medycznej ani porad medycznych. Wszystkie tre\u015bci \u2014 w tym statystyki, serie, podsumowania, wyzwania i komunikaty motywacyjne \u2014 maj\u0105 wy\u0142\u0105cznie og\u00f3lny, motywacyjny charakter.",
          "Przed rozpocz\u0119ciem lub zmian\u0105 aktywno\u015bci fizycznej skonsultuj si\u0119 z lekarzem, zw\u0142aszcza je\u015bli masz jakiekolwiek problemy zdrowotne. Natychmiast przerwij \u0107wiczenia, je\u015bli poczujesz b\u00f3l, zawroty g\u0142owy lub dyskomfort.",
        ],
      },
      {
        h: "3. Twoja odpowiedzialno\u015b\u0107",
        p: [
          "Korzystasz z aplikacji i podejmujesz aktywno\u015b\u0107 fizyczn\u0105 na w\u0142asn\u0105 odpowiedzialno\u015b\u0107 i w granicach w\u0142asnych mo\u017cliwo\u015bci.",
          "Odpowiadasz za tre\u015bci, kt\u00f3re wprowadzasz (w tym notatki i nazw\u0119 wy\u015bwietlan\u0105), oraz za zabezpieczenie urz\u0105dzenia, na kt\u00f3rym zapisany jest Tw\u00f3j lokalny profil. Aplikacja u\u017cywa profilu powi\u0105zanego z urz\u0105dzeniem, bez has\u0142a \u2014 ka\u017cda osoba z dost\u0119pem do Twojego urz\u0105dzenia i przegl\u0105darki mo\u017ce uzyska\u0107 dost\u0119p do profilu.",
        ],
      },
      {
        h: "4. Zasady spo\u0142eczno\u015bci",
        p: [
          "Z funkcji spo\u0142eczno\u015bciowych (udost\u0119pnione wpisy, kudos, obserwowanie, wyzwania) nale\u017cy korzysta\u0107 z szacunkiem. Zabronione jest publikowanie tre\u015bci bezprawnych, obra\u017aliwych, nienawistnych, dyskryminuj\u0105cych, wprowadzaj\u0105cych w b\u0142\u0105d lub spamu.",
          "Mo\u017cemy usuwa\u0107 tre\u015bci lub profile naruszaj\u0105ce te zasady oraz ogranicza\u0107 dost\u0119p do funkcji spo\u0142eczno\u015bciowych w przypadku nadu\u017cy\u0107.",
        ],
      },
      {
        h: "5. Dost\u0119pno\u015b\u0107 i ograniczenia",
        p: [
          "Us\u0142uga jest \u015bwiadczona w stanie \u201etakim, jaka jest\u201d i \u201ew miar\u0119 dost\u0119pno\u015bci\u201d. Mo\u017cemy zmienia\u0107, zawiesza\u0107 lub wycofywa\u0107 dowolne funkcje.",
          "Do\u0142o\u017cymy starannych wysi\u0142k\u00f3w, aby us\u0142uga by\u0142a dost\u0119pna, a dane bezpieczne, ale nie gwarantujemy nieprzerwanej dost\u0119pno\u015bci. Aplikacja nie oferuje obecnie eksportu danych. Usuni\u0119cie profilu trwale usuwa Twoje dane (zobacz Polityk\u0119 prywatno\u015bci i stron\u0119 Usuwanie danych).",
        ],
      },
      {
        h: "6. W\u0142asno\u015b\u0107 intelektualna",
        p: [
          "Aplikacja, jej projekt, marka (w tym nazwy \u201eBlue Ocean Motion\u201d i \u201eMotion Card\u201d) oraz wszystkie powi\u0105zane materia\u0142y nale\u017c\u0105 do operatora.",
          "Zachowujesz prawa do tworzonych przez siebie tre\u015bci (np. notatek do aktywno\u015bci). W\u0142\u0105czaj\u0105c udost\u0119pnianie, udzielasz nam niewy\u0142\u0105cznej licencji na wy\u015bwietlanie tych tre\u015bci w funkcjach spo\u0142eczno\u015bciowych aplikacji.",
        ],
      },
      {
        h: "7. Odpowiedzialno\u015b\u0107",
        p: [
          "W maksymalnym zakresie dozwolonym przez prawo nie ponosimy odpowiedzialno\u015bci za szkody po\u015brednie ani za skutki aktywno\u015bci fizycznej podejmowanej przez u\u017cytkownika. \u017badne postanowienie Regulaminu nie wy\u0142\u0105cza odpowiedzialno\u015bci, kt\u00f3rej nie mo\u017cna wy\u0142\u0105czy\u0107 na podstawie bezwzgl\u0119dnie obowi\u0105zuj\u0105cych przepis\u00f3w prawa polskiego, w tym praw konsumenta.",
        ],
      },
      {
        h: "8. Prawo w\u0142a\u015bciwe",
        p: [
          "Regulamin podlega prawu polskiemu.",
          "Je\u017celi korzystasz z us\u0142ugi jako konsument, wyb\u00f3r prawa polskiego nie pozbawia Ci\u0119 ochrony przyznanej przez bezwzgl\u0119dnie obowi\u0105zuj\u0105ce przepisy prawa pa\u0144stwa Twojego zwyk\u0142ego pobytu. Zawsze zachowujesz wszystkie uprawnienia wynikaj\u0105ce z bezwzgl\u0119dnie obowi\u0105zuj\u0105cych przepis\u00f3w prawa konsumenckiego.",
          "W\u0142a\u015bciwo\u015b\u0107 s\u0105du w sporach okre\u015blaj\u0105 bezwzgl\u0119dnie obowi\u0105zuj\u0105ce przepisy prawa. W szczeg\u00f3lno\u015bci \u017cadne postanowienie Regulaminu nie zobowi\u0105zuje konsumenta do prowadzenia post\u0119powania wy\u0142\u0105cznie przed s\u0105dami polskimi, je\u017celi w\u0142a\u015bciwe przepisy pozwalaj\u0105 konsumentowi pozywa\u0107 lub by\u0107 pozywanym przed s\u0105dami pa\u0144stwa jego zwyk\u0142ego pobytu.",
        ],
      },
      {
        h: "9. Zmiany Regulaminu",
        p: [
          "Mo\u017cemy aktualizowa\u0107 Regulamin. Aktualna wersja wraz z dat\u0105 aktualizacji jest zawsze dost\u0119pna pod tym adresem. Dalsze korzystanie z us\u0142ugi po zmianach oznacza akceptacj\u0119 zaktualizowanego Regulaminu.",
        ],
      },
      {
        h: "10. Kontakt",
        p: [`${LEGAL_META.operator}`, `E-mail: ${LEGAL_META.email}`],
      },
    ],
  },
};

// ---------------------------------------------------------- DATA DELETION
export const deleteDataContent = {
  en: {
    title: "Data Deletion",
    sections: [
      {
        h: "1. How your data works",
        p: [
          "Blue Ocean Motion does not use a traditional username/password account. Your profile is identified by a random identifier stored in your browser, and the associated data \u2014 profile details, activities, community posts, kudos, follow relationships and challenge progress \u2014 is stored on our servers.",
        ],
      },
      {
        h: "2. Delete everything from within the app (recommended)",
        p: ["Open the app \u2192 go to Profile \u2192 tap \"Delete profile and data\" \u2192 confirm. This immediately and permanently deletes from the active production database:"],
        list: [
          "your profile (name, display name, bio, goal, settings)",
          "all logged activities",
          "all your community posts",
          "all kudos you gave and received",
          "all follow relationships (both directions)",
          "all challenge memberships, progress and celebration state",
        ],
        after: [
          "The profile identifier stored in your browser is also cleared, and the app returns to the start screen.",
        ],
      },
      {
        h: "3. Note about \"Start over\"",
        p: [
          "The separate \"Start over\" option only disconnects the profile from your device. It does NOT delete data from our servers. For real deletion always use \"Delete profile and data\".",
        ],
      },
      {
        h: "4. If you no longer have access to your device or profile",
        p: [
          `Write to us at ${LEGAL_META.email}. Describe your public display name (if you used the community) and the approximate period you used the app. We will verify the request and delete the associated data.`,
        ],
      },
      {
        h: "5. Timing",
        p: [
          "In-app deletion takes effect immediately in the active production database: the records listed above are removed at once and are not used or restored afterwards.",
          "The application does not create its own backup copies. Technical server logs (which may temporarily include IP addresses) and any short-lived infrastructure-level copies maintained by the hosting platform for reliability are automatically rotated and overwritten in the normal operational cycle; they are not used to reconstruct deleted profiles.",
        ],
      },
      {
        h: "6. Contact",
        p: [`${LEGAL_META.operator}`, `E-mail: ${LEGAL_META.email}`],
      },
    ],
  },
  pl: {
    title: "Usuwanie danych",
    sections: [
      {
        h: "1. Jak dzia\u0142aj\u0105 Twoje dane",
        p: [
          "Blue Ocean Motion nie u\u017cywa tradycyjnego konta z loginem i has\u0142em. Tw\u00f3j profil jest identyfikowany losowym identyfikatorem zapisanym w przegl\u0105darce, a powi\u0105zane dane \u2014 dane profilu, aktywno\u015bci, wpisy w spo\u0142eczno\u015bci, kudos, relacje obserwowania i post\u0119p w wyzwaniach \u2014 s\u0105 przechowywane na naszych serwerach.",
        ],
      },
      {
        h: "2. Usu\u0144 wszystko z poziomu aplikacji (zalecane)",
        p: ["Otw\u00f3rz aplikacj\u0119 \u2192 przejd\u017a do Profilu \u2192 wybierz \u201eUsu\u0144 profil i dane\u201d \u2192 potwierd\u017a. To natychmiast i trwale usuwa z aktywnej produkcyjnej bazy danych:"],
        list: [
          "Tw\u00f3j profil (imi\u0119, nazwa wy\u015bwietlana, bio, cel, ustawienia)",
          "wszystkie zapisane aktywno\u015bci",
          "wszystkie Twoje wpisy w spo\u0142eczno\u015bci",
          "wszystkie kudos \u2014 dane i otrzymane",
          "wszystkie relacje obserwowania (w obu kierunkach)",
          "cz\u0142onkostwo w wyzwaniach, post\u0119p i stan celebracji",
        ],
        after: [
          "Identyfikator profilu zapisany w przegl\u0105darce r\u00f3wnie\u017c zostaje wyczyszczony, a aplikacja wraca do ekranu startowego.",
        ],
      },
      {
        h: "3. Uwaga dotycz\u0105ca \u201eZacznij od nowa\u201d",
        p: [
          "Osobna opcja \u201eZacznij od nowa\u201d jedynie od\u0142\u0105cza profil od Twojego urz\u0105dzenia. NIE usuwa danych z naszych serwer\u00f3w. Aby naprawd\u0119 usun\u0105\u0107 dane, zawsze u\u017cywaj \u201eUsu\u0144 profil i dane\u201d.",
        ],
      },
      {
        h: "4. Je\u015bli nie masz ju\u017c dost\u0119pu do urz\u0105dzenia lub profilu",
        p: [
          `Napisz do nas na adres ${LEGAL_META.email}. Opisz swoj\u0105 publiczn\u0105 nazw\u0119 wy\u015bwietlan\u0105 (je\u015bli korzysta\u0142e\u015b/a\u015b ze spo\u0142eczno\u015bci) oraz przybli\u017cony okres korzystania z aplikacji. Zweryfikujemy zg\u0142oszenie i usuniemy powi\u0105zane dane.`,
        ],
      },
      {
        h: "5. Czas realizacji",
        p: [
          "Usuni\u0119cie z poziomu aplikacji dzia\u0142a natychmiast w aktywnej produkcyjnej bazie danych: wymienione wy\u017cej dane s\u0105 usuwane od razu i nie s\u0105 p\u00f3\u017aniej wykorzystywane ani przywracane.",
          "Aplikacja nie tworzy w\u0142asnych kopii zapasowych. Techniczne logi serwera (kt\u00f3re mog\u0105 tymczasowo zawiera\u0107 adresy IP) oraz ewentualne kr\u00f3tkotrwa\u0142e kopie infrastrukturalne utrzymywane przez platform\u0119 hostingow\u0105 dla niezawodno\u015bci s\u0105 automatycznie rotowane i nadpisywane w normalnym cyklu operacyjnym; nie s\u0142u\u017c\u0105 one do odtwarzania usuni\u0119tych profili.",
        ],
      },
      {
        h: "6. Kontakt",
        p: [`${LEGAL_META.operator}`, `E-mail: ${LEGAL_META.email}`],
      },
    ],
  },
};
